import {RRCalendarDayType} from "../../types/RRCalendarDayType";
import {Badge, Col, FormCheck, FormControl, FormGroup, Modal, Row} from "react-bootstrap";
import {useEffect, useState} from "react";
import {CancelIconButton, SaveIconButton} from "../../components/IconButtons";
import {dateToString, timeToString} from "../../tools";
import {RREventType} from "../../types/RREventType";
import ReactDatePicker from "react-datepicker";
import {MaybeDate} from "../../types/Maybe";
import {createCalendarPlan} from "../../calendar/scheduleMutations";
import {eventPeriodError} from "../../calendar/eventPeriod";
import {useAppSelector} from "../../hooks";
import {selectCurrentEmployees} from "../../../features/AppDataSlice";
import {useWarehouseLocks, WarehouseLockNotice} from "../../warehouse/useWarehouseLocks";
import {selectCurrentEmployee} from "../../../features/AppStateSlice";
import {updateCalendarItemsWithChangeLog} from "../../views/calendar/change-log/calendarChangeLog";

type MCalendarItemStartProps = {
    selectedDate: Date;
    event: RREventType;
    item: RRCalendarDayType;
    readonly: boolean;
    onDone: () => void;
}

const MCalendarItemStart: React.FC<MCalendarItemStartProps> = ({selectedDate, event, item, readonly: originalReadonly, onDone}) => {
    const allEmployees = useAppSelector(selectCurrentEmployees);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const itemDepartment = item.department.toUpperCase();
    const employees =
        allEmployees.filter(e => {
            switch (itemDepartment) {
                case "MONTAGE":
                    return e.isMontage;
                case "CATERING":
                    return e.isCatering;
                case "STORE":
                    return e.isStore;
                case "LOGISTICS":
                    return e.isLogistics;
                default:
                    return false;
            }
        }).sort((a, b) => a.name.localeCompare(b.name));
    const [newItem, setNewItem] = useState<RRCalendarDayType | null>({...item});

    const defaultStartTime = selectedDate ? new Date(selectedDate) : new Date(item.eventDayStart);
    defaultStartTime.setHours(item.eventDayStart.getHours());
    defaultStartTime.setMinutes(item.eventDayStart.getMinutes());

    const [startTime, setStartTime] = useState<MaybeDate>(defaultStartTime);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const warehouseStatus = useWarehouseLocks();
    const originalAccess = warehouseStatus(item.eventDayStart, item.eventId, item.department);
    const targetAccess = warehouseStatus(startTime, item.eventId, item.department);
    const lockAccess = originalAccess.blocked ? originalAccess : targetAccess;
    const readonly = originalReadonly || lockAccess.blocked;

    useEffect(() => {
        setNewItem({...item});
    }, [item]);

    function updateStartTime(e: Date | null) {
        if (!e || !startTime) return;

        const date = new Date(startTime);
        date.setHours(e.getHours());
        date.setMinutes(e.getMinutes());
        date.setSeconds(0);
        setStartTime(date);
    }

    async function onSaveStartItem() {
        if (readonly || saving) return;
        if (!startTime) {
            setError("Въведете начален час.");
            return;
        }
        if (event.id !== "WAREHOUSE") {
            const periodError = eventPeriodError(startTime, event.eventName, event.firstDate, event.lastDate);
            if (periodError) {
                setError(periodError);
                return;
            }
        }
        if (newItem) {
            const newData = {
                eventDayStart: startTime,
                description: newItem.description ?? null,
                isSupervisor: newItem.isSupervisor ?? false,
                isMontage: newItem.isMontage ?? false,
            }

            try {
                setSaving(true);
                if (!newItem.ref) {
                    await createCalendarPlan({...newItem, ...newData});
                } else {
                    await updateCalendarItemsWithChangeLog([{
                        ref: newItem.ref,
                        data: newData,
                        context: {eventNumber: event.eventNumber, eventName: event.eventName},
                    }], currentEmployee);
                }
                onDone();
            } catch (e) {
                setError(e instanceof Error ? e.message : "Неуспешен запис.");
            } finally {
                setSaving(false);
            }
        }
    }

    return (
        <Modal show={true}>
            <Modal.Body>
                <WarehouseLockNotice access={lockAccess}/>
                {
                    newItem?.employeeId ?
                        <Row className={"fs-4 fw-bold"}>
                            <Col>{newItem.employeeName}</Col>
                            <Col className={"text-end"} xs={"auto"}>{newItem.isSupervisor &&
                                <Badge pill bg="primary">S</Badge>}</Col>
                        </Row> :
                        <Row className={"fs-4 fw-bold"}>
                            <FormGroup>
                                <FormControl as={"select"} disabled={readonly} value={newItem?.employeeId}
                                             onChange={e => {
                                                 const employeeId = e.target.value;
                                                 const employee = employees.find(e => e.id === employeeId);
                                                 setNewItem({
                                                     ...newItem!,
                                                     employeeId: employeeId,
                                                     employeeName: employee?.name || "",
                                                 });
                                             }}>
                                    <option value={""}>Изберете служител</option>
                                    {
                                        employees.map(e => (
                                            <option key={e.id} value={e.id}>{e.name}</option>
                                        ))
                                    }
                                </FormControl>
                            </FormGroup>
                        </Row>
                }
                <Row className={"small text-primary mt-3 pt-3 border-0 border-top"}>
                    <Col>
                        <div><b>{event.eventName}</b></div>
                        <div className={"mt-2"}>
                            {
                                [event.place, event.clientName, event.clientPhone].filter(
                                    item => item !== "" && item !== event.eventName
                                ).join(", ")
                            }
                        </div>
                    </Col>
                    <Col xs={"auto"}>
                        <i>От: {dateToString(event.firstDate)}</i>
                        {
                            event.lastDate && <><br/><i>До: {dateToString(event.lastDate)}</i></>
                        }
                    </Col>
                </Row>

                <Row>
                    <Col xs={"3"}>
                        <Row className={"mt-3"}>
                            <Col xs={"12"} className={"lh-2 me-0 pe-0"}><b>Начален час:</b></Col>
                            <Col xs={"auto"} className={"overflow-hidden"}>
                                <ReactDatePicker
                                    onChange={e => {
                                        updateStartTime(e)
                                    }}
                                    disabled={readonly}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={30}
                                    timeCaption="Начало:"
                                    dateFormat="HH:mm"
                                    className={"form-control-sm w-50px"}
                                    value={timeToString(startTime)}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Row className={"mt-3"}>
                            <Col xs={"12"} className={"lh-2 me-0 pe-0"}><b>Роля на служителя:</b></Col>
                            <Col xs={"6"}>
                                <FormCheck checked={newItem?.isSupervisor} disabled={readonly}
                                           label={"Супервизор"}
                                           onChange={e => setNewItem({...newItem!, isSupervisor: e.target.checked})}/>
                            </Col>
                            <Col xs={"6"}>
                                <FormCheck checked={newItem?.isMontage} disabled={readonly}
                                           label={"Служител"}
                                           onChange={e => setNewItem({...newItem!, isMontage: e.target.checked})}/>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row>
                    <Col xs={"12"} className={"mt-3"}>
                        <b>Бележки:</b>
                    </Col>
                    <Col xs={"12"}>
                        <FormControl as={"textarea"} disabled={readonly} value={newItem?.description}
                                     onChange={e => setNewItem({...newItem!, description: e.target.value})}/>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                {
                    error &&
                    <Row>
                        <Col>
                            <Badge bg={"danger"}>{error}</Badge>
                        </Col>
                    </Row>
                }
                <Row>
                    {
                        !readonly &&
                        <Col className={"text-center"}>
                            <SaveIconButton
                                onClick={onSaveStartItem}
                                disabled={newItem?.employeeId === "" || saving}
                            />
                        </Col>
                    }
                    <Col>
                        <CancelIconButton
                            onClick={() => onDone()}
                            disabled={saving}
                        />
                    </Col>
                </Row>
            </Modal.Footer>
        </Modal>
    )
}

export default MCalendarItemStart;
